import { Flex, Image, Layout, Menu, Typography, Drawer, Button } from 'antd';
import { VscLayout } from 'react-icons/vsc';
import { FaBook, FaCalendarAlt, FaChartBar, FaCog, FaEllipsisH, FaFileAlt, FaList, FaStore, FaUsers } from 'react-icons/fa';
import { GrTransaction } from 'react-icons/gr';
import { TbLogout2 } from 'react-icons/tb';
import { useEffect, useState, useLayoutEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import smallLogo from '../../assets/small_logo.png';
import largeLogo from '../../assets/large_logo.png';
import { BiUser } from 'react-icons/bi';
import { MdEmail } from 'react-icons/md';
import { AmazonOutlined, ApiOutlined, GoogleOutlined, PhoneOutlined, WifiOutlined } from '@ant-design/icons';

const Sidebar = ({ collapsed, drawerVisible, setDrawerVisible}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedMenu, setSelectedMenu] = useState('dashboard');
  const [openKeys, setOpenKeys] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 550);

  let iconStyle = {
    position: 'relative',
    top: collapsed ? '3.5px' : 0,
    left: collapsed ? '-3px' : 0,
  };
  
  let iconProps = {
    style: iconStyle,
    size: 18,
  };

  /**
   * Which sidebar entry a route belongs to.
   *
   * Each section is one page with tabs now, but the individual routes still
   * exist so old bookmarks keep working — this is what makes one of those
   * still highlight the right entry instead of nothing.
   */
  const SECTION_FOR = {
    management: ['management', 'roles', 'users', 'teams', 'all-requests', 'all-contacts', 'newsletters', 'account-deletions'],
    'website-app': ['website-app', 'categories', 'books', 'other-books', 'language-guides', 'bundles', 'coupons', 'companion-offers'],
    'affiliates-section': ['affiliates-section', 'affiliate-programme', 'affiliates', 'link-approvals', 'api-access-approvals'],
    'api-access-section': ['api-access-section', 'api-access'],
  };

  useEffect(() => {
    const path = location.pathname.split('/');
    const menuKey = (path[1] === 'admin-dashboard' || path[1] === 'dashboard') ? 'admin-dashboard' : (path[1] || 'admin-dashboard');

    // Nothing nests any more, so there is no submenu to open — just the one
    // entry that owns this route.
    const section = Object.keys(SECTION_FOR).find((key) => SECTION_FOR[key].includes(menuKey));
    setSelectedMenu(section || menuKey);
    setOpenKeys([]);
  }, [location.pathname]);

  useLayoutEffect(() => {
    const updateIsMobile = () => {
      setIsMobile(window.innerWidth < 550);
    };

    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);


  const onClose = () => {
    setDrawerVisible(false);
  };

  const handleMenuChange = ({ key, keyPath }) => {
    navigate('/' + key);
    setSelectedMenu(key);
    if (isMobile) {
      onClose();
    }
  };

  const logoutClick = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.reload()
  };

  /**
   * The sidebar, in the order the work is actually done.
   *
   * Four sections that were submenus are single tabbed pages now — a submenu
   * costs a click to open and a page load to read, and these are all one
   * subject each. The routes underneath still exist for old bookmarks.
   *
   * Transactions, Telna eSIMs and Settings sit at the bottom under "Others":
   * they are referred to occasionally rather than worked in.
   */
  const menuItems = [
    {
      key: 'admin-dashboard',
      icon: <FaChartBar {...iconProps} />,
      label: 'Dashboard',
    },
    {
      key: 'management',
      icon: <BiUser {...iconProps} />,
      label: 'Management',
    },
    {
      key: 'website-app',
      icon: <FaBook {...iconProps} />,
      label: 'Website & App',
    },
    {
      key: 'amazon-purchases',
      icon: <AmazonOutlined style={iconStyle} />,
      label: 'Amazon Purchases',
    },
    {
      key: 'affiliates-section',
      icon: <FaUsers {...iconProps} />,
      label: 'Affiliates',
    },
    {
      key: 'website-orders',
      icon: <FaFileAlt {...iconProps} />,
      label: 'Website Orders',
    },
    {
      key: 'google-drive',
      icon: <GoogleOutlined style={iconStyle} />,
      label: 'Google Drive',
    },
    {
      key: 'api-access-section',
      icon: <ApiOutlined style={iconStyle} />,
      label: 'API Access',
    },
    {
      key: 'vendors',
      icon: <FaStore {...iconProps} />,
      label: 'Vendors',
    },
    {
      key: 'others',
      icon: <FaEllipsisH {...iconProps} />,
      label: 'Others',
      children: [
        { key: 'transactions', label: 'Transactions' },
        // Second eSIM provider, separate from the eSIM Access orders under
        // Website Orders. Its inventory, catalogue and orders are its own.
        { key: 'telna-esims', label: 'Telna eSIMs' },
        // The mobile app and /new-esims. Separate from Telna eSIMs: that page is
        // the provider's inventory, this is the product sold on top of it.
        { key: 'youguide-one', label: 'YouGuide One' },
        { key: 'settings', label: 'Settings' },
      ],
    },
  ];

  const logoutItem = {
    key: 'logout',
    icon: <TbLogout2 {...iconProps} />,
    label: (
      <Link onClick={logoutClick} style={{ color: 'inherit', textDecoration: 'none' }}>
        Logout
      </Link>
    ),
    danger: true,
  };

  return (
    <div>
      {isMobile ? (
        <>
          <Drawer
            title="Menu"
            placement="left"
            closable={true}
            onClose={onClose}
            open={drawerVisible}
            bodyStyle={{ padding: 0 }}
          >
            <Menu
              onClick={handleMenuChange}
              mode="inline"
              className="custom_menu_sidebar"
              selectedKeys={[selectedMenu]}
              defaultSelectedKeys={['admin-dashboard']}
              openKeys={openKeys}
              onOpenChange={setOpenKeys}
              items={[...menuItems, logoutItem]}
            />
          </Drawer>
        </>
      ) : (
        <Layout.Sider
          width={220}
          theme="light"
          trigger={null}
          collapsible
          collapsed={collapsed}
          style={{ minHeight: '100vh' }}
        >
          <Flex
            style={{ padding: '20px 20px 5px 20px' }}
            justify={collapsed ? 'center' : 'start'}
          >
            <Typography.Title level={3}>
              {collapsed ? (
                <Image src={smallLogo} preview={false} style={{ marginTop: '-5px', height: '45px'}} />
              ) : (
                <Image src={largeLogo}  style={{ marginTop: '-5px', height: '45px'}} preview={false} />
              )}
            </Typography.Title>
          </Flex>
          <Flex
            vertical
            justify="space-between"
            style={{ height: 'calc(100vh - 90px)' }}
          >
            <Menu
              onClick={handleMenuChange}
              mode="inline"
              className="custom_menu_sidebar"
              selectedKeys={[selectedMenu]}
              defaultSelectedKeys={['admin-dashboard']}
              openKeys={openKeys}
              onOpenChange={setOpenKeys}
              items={menuItems}
            />
            <Menu
              mode="inline"
              defaultSelectedKeys={['admin-dashboard']}
              items={[logoutItem]}
            />
          </Flex>
        </Layout.Sider>
      )}
    </div>
  );
};

export default Sidebar;
